(() => {
  if (window.neDripCommerceReady) return;
  window.neDripCommerceReady = true;
  const rootPath = window.Shopify?.routes?.root || '/';
  const endpoint = (path) => `${rootPath.replace(/\/$/, '')}/${path}`;
  const pendingProducts = new WeakSet();
  const pendingVariants = new WeakSet();
  let cartPending = false;

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.dataset.cartCount = String(count);
    });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: count } }));
  }

  function status(element, message, error = false) {
    if (!element) return;
    element.textContent = message;
    element.classList.toggle('is-error', error);
  }

  async function readResponse(response) {
    const data = await response.json();
    if (!response.ok || data.status >= 400) {
      const message = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : '';
      throw new Error(message);
    }
    return data;
  }

  function isPlainClick(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  document.addEventListener('click', event => {
    const link = event.target.closest('[data-gallery-page]');
    if (!link || !isPlainClick(event)) return;
    const target = document.getElementById(link.hash.slice(1));
    const gallery = target?.closest('.product-gallery');
    if (!gallery || !window.matchMedia('(max-width: 749px)').matches) return;
    event.preventDefault();
    gallery.scrollTo({ left: gallery.scrollLeft + target.getBoundingClientRect().left - gallery.getBoundingClientRect().left, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  });
  document.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-option-link]');
    if (!link || !isPlainClick(event)) return;
    const productRoot = link.closest('[data-product-root]');
    if (!productRoot) return;
    const url = new URL(link.href, window.location.origin);
    const currentProduct = new URL(productRoot.dataset.productUrl, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname !== currentProduct.pathname) return;
    event.preventDefault();
    if (pendingVariants.has(productRoot) || pendingProducts.has(productRoot)) return;
    pendingVariants.add(productRoot);
    productRoot.setAttribute('aria-busy', 'true');
    const focusId = link.id;
    const previousQuantity = productRoot.querySelector('[name="quantity"]')?.value;
    const requestUrl = new URL(url);
    requestUrl.searchParams.set('section_id', productRoot.dataset.sectionId);
    try {
      const response = await fetch(requestUrl, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error();
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = html.querySelector('[data-product-root]');
      if (!replacement) throw new Error();
      const quantity = replacement.querySelector('[name="quantity"]');
      if (quantity && previousQuantity) {
        const defaultQuantity = quantity.value;
        quantity.value = previousQuantity;
        if (!quantity.checkValidity()) quantity.value = defaultQuantity;
      }
      productRoot.replaceWith(replacement);
      window.history.replaceState(window.history.state, '', url);
      const selectedLink = document.getElementById(focusId) || replacement.querySelector('[data-option-link][aria-current]');
      selectedLink?.focus({ preventScroll: true });
      const variantFeedback = replacement.querySelector('[data-product-status]');
      const variantPrice = replacement.querySelector('.product-info__price')?.textContent.trim();
      const variantAvailability = replacement.querySelector('.product-availability')?.textContent.trim();
      status(variantFeedback, [variantPrice, variantAvailability].filter(Boolean).join(' · '));
      const mediaId = replacement.querySelector('[data-product-info]')?.dataset.featuredMediaId;
      const selectedMedia = mediaId && replacement.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
      const gallery = replacement.querySelector('.product-gallery');
      if (selectedMedia && gallery && window.matchMedia('(max-width: 749px)').matches) gallery.scrollTo({ left: selectedMedia.offsetLeft - gallery.offsetLeft, behavior: 'instant' });
      window.Shopify?.PaymentButton?.init?.();
    } catch {
      window.location.assign(url.href);
    } finally {
      pendingVariants.delete(productRoot);
      productRoot.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    const productRoot = form.closest('[data-product-root]');
    if (!productRoot) return;
    event.preventDefault();
    if (pendingProducts.has(productRoot) || pendingVariants.has(productRoot)) return;
    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput?.value || variantInput.disabled || !form.reportValidity()) return;
    const feedback = form.querySelector('[data-product-status]');
    const addButtons = Array.from(productRoot.querySelectorAll('button[name="add"]'));
    const cartLink = form.querySelector('[data-cart-success]');
    const formData = new FormData(form);
    pendingProducts.add(productRoot);
    form.setAttribute('aria-busy', 'true');
    addButtons.forEach(button => { button.disabled = true; });
    const stickyStatus = productRoot.querySelector('[data-sticky-status]');
    const stickyCart = productRoot.querySelector('[data-sticky-cart]');
    if (stickyCart) stickyCart.hidden = true;
    status(stickyStatus, feedback?.dataset.adding || '');
    if (cartLink) cartLink.hidden = true;
    status(feedback, feedback?.dataset.adding || '');
    try {
      await readResponse(await fetch(endpoint('cart/add.js'), { method: 'POST', body: formData, credentials: 'same-origin', headers: { Accept: 'application/json' } }));
      status(feedback, feedback?.dataset.added || '');
      if (cartLink) cartLink.hidden = false;
      if (stickyCart) stickyCart.hidden = false;
      status(stickyStatus, feedback?.dataset.added || '');
      try {
        const cart = await readResponse(await fetch(endpoint('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } }));
        updateCartCount(cart.item_count);
      } catch {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (error) {
      status(feedback, error.message || feedback?.dataset.error || '', true);
      status(stickyStatus, error.message || feedback?.dataset.error || '', true);
    } finally {
      pendingProducts.delete(productRoot);
      form.removeAttribute('aria-busy');
      addButtons.forEach(button => { button.disabled = false; });
    }
  });

  function sameProperties(first = {}, second = {}) {
    const firstKeys = Object.keys(first).sort();
    const secondKeys = Object.keys(second).sort();
    return firstKeys.length === secondKeys.length && firstKeys.every((key, index) => key === secondKeys[index] && first[key] === second[key]);
  }

  async function changeCart(cartRoot, operations, note) {
    if (cartPending) return;
    cartPending = true;
    const feedback = cartRoot.querySelector('[data-cart-status]');
    const cartForm = cartRoot.querySelector('[data-cart-form]');
    const buttons = Array.from(cartForm?.querySelectorAll('button') || []);
    const inputs = Array.from(cartForm?.querySelectorAll('input, textarea') || []);
    status(feedback, feedback?.dataset.updating || '');
    cartRoot.setAttribute('aria-busy', 'true');
    buttons.forEach((button) => { button.disabled = true; });
    inputs.forEach((input) => { input.readOnly = true; });
    const sectionId = cartRoot.dataset.sectionId;
    let latestCart;
    const request = async (path, body) => readResponse(await fetch(endpoint(path), { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...body, sections: [sectionId], sections_url: window.location.pathname }) }));
    const renderCart = (cart, message, error = false) => {
      updateCartCount(cart.item_count);
      const sectionHtml = cart.sections?.[sectionId];
      const replacement = sectionHtml && new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('[data-cart-root]');
      if (!replacement) return false;
      cartRoot.replaceWith(replacement);
      const newFeedback = replacement.querySelector('[data-cart-status]');
      status(newFeedback, message || newFeedback?.dataset.updated || '', error);
      newFeedback?.setAttribute('tabindex', '-1');
      newFeedback?.focus({ preventScroll: true });
      return true;
    };
    try {
      for (const operation of operations) {
        let key = operation.key;
        if (latestCart) {
          const item = latestCart.items.find((item) => item.key === key) || latestCart.items.find((item) => String(item.variant_id) === operation.variantId && sameProperties(item.properties || {}, operation.properties || {}) && String(item.selling_plan_allocation?.selling_plan?.id || '') === operation.sellingPlanId);
          if (!item) throw new Error(feedback?.dataset.error || '');
          key = item.key;
        }
        latestCart = await request('cart/change.js', { id: key, quantity: operation.quantity });
      }
      if (note !== undefined) latestCart = await request('cart/update.js', { note });
      if (!latestCart) {
        status(feedback, feedback?.dataset.updated || '');
        return;
      }
      if (!renderCart(latestCart)) {
        window.location.reload();
      }
    } catch (error) {
      const message = error.message || feedback?.dataset.error || '';
      if (latestCart) {
        if (!renderCart(latestCart, message, true)) window.location.reload();
      } else status(feedback, message, true);
    } finally {
      cartPending = false;
      cartRoot.removeAttribute('aria-busy');
      buttons.forEach((button) => { button.disabled = false; });
      inputs.forEach((input) => { input.readOnly = false; });
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-cart-form]');
    if (!form || event.submitter?.name === 'checkout') return;
    event.preventDefault();
    if (cartPending || !form.reportValidity()) return;
    const cartRoot = form.closest('[data-cart-root]');
    const operations = Array.from(form.querySelectorAll('[data-cart-quantity]')).filter((input) => input.value !== input.dataset.originalQuantity).map((input) => ({ key: input.dataset.key, quantity: Number(input.value), variantId: input.dataset.variantId, properties: JSON.parse(input.dataset.properties || '{}') || {}, sellingPlanId: input.dataset.sellingPlanId || '' }));
    const note = form.querySelector('[name="note"]');
    changeCart(cartRoot, operations, note?.value);
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cart-remove]');
    if (!link || !isPlainClick(event)) return;
    const cartRoot = link.closest('[data-cart-root]');
    if (!cartRoot) return;
    event.preventDefault();
    changeCart(cartRoot, [{ key: link.dataset.key, quantity: 0 }]);
  });

  const observedRecommendations = new WeakSet();
  function initRecommendations(scope = document) {
    scope.querySelectorAll('[data-recommendations]').forEach((element) => {
      if (observedRecommendations.has(element)) return;
      observedRecommendations.add(element);
      const load = async () => {
        if (!element.isConnected) return;
        try {
          const response = await fetch(element.dataset.recommendationsUrl, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = html.querySelector('[data-recommendations]');
          if (content) element.innerHTML = content.innerHTML;
        } catch {}
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          load();
        }, { rootMargin: '0px 0px 300px 0px' });
        observer.observe(element);
      } else load();
    });
  }
  initRecommendations();
  document.addEventListener('shopify:section:load', (event) => initRecommendations(event.target));
})();
